<template>
	<div class="rentabilidad_tab report_theme_container" data-theme="rentabilidad">
		<!-- Mismo set de sub_nav_filters (Mensual/Trimestral/YTD/Anual) que
			ResultadosTab. Comparten el `filterPeriod` del store financialReports
			→ cambiar el bucket acá lo refleja también en Resultados al volver. -->
		<TabHeader
			title="Rentabilidad"
			description="Analiza la tendencia y composición de la rentabilidad de tu negocio"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth" />

		<!-- ================================================================ -->
		<!-- Resumen de Ventas & Márgenes                                      -->
		<!-- 3 cards mostrando el MISMO total (ventas) bajo 3 ventanas          -->
		<!-- temporales (mes / YTD / LTM). El breakdown descompone el total     -->
		<!-- en los 5 escalones de margen — cada item tiene amount + percent.   -->
		<!-- ================================================================ -->
		<div class="section_title mt-5 mb-4">
			<Layers class="section_icon mr-2" />
			Resumen de Ventas & Márgenes
		</div>

		<div class="cards_container is-flex">
			<CardVentas v-for="(card, index) in profitabilityCards" :key="index" v-bind="card" class="mr-4" />
		</div>

		<!-- ================================================================ -->
		<!-- Evolución de Rentabilidad                                         -->
		<!-- Reusa FinancialResultsTable (misma usada por ResultadosTab) con    -->
		<!-- las filas filtradas y reordenadas para mostrar Ventas + 5          -->
		<!-- escalones de margen, cada uno con su pareja money/%.               -->
		<!-- ================================================================ -->
		<FinancialResultsTable
			class="mt-6"
			:rows="profitabilityRows"
			:headers="getHeadersTable"
			:isLoading="isLoading"
			:selectedPeriodRange="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			@update-period="updateSelectedPeriodRange">
			<template #title>
				<ChartLine class="title_icon mr-2" />
				<span class="tittle_section">Evolución de Rentabilidad</span>
			</template>
			<template #header-actions>
				<!-- Controles placeholders. Comportamiento (gráfico comparado,
					vista de barras) queda pendiente de definición. -->
				<button class="action_btn mr-2" disabled>
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Comparación
				</button>
				<button class="action_btn mr-2" disabled>
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Ver Barra
				</button>
			</template>
		</FinancialResultsTable>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import ChartLine from "@/components/svg/ChartLine.vue";
import Layers from "@/components/svg/Layers.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";
import {
	PERIOD_TYPES,
	FILTER_PERIOD_OPTIONS,
	PROFITABILITY_CARD_PROGRESS_COLORS,
} from "../config/reportStructure";
import { formatPercent, formatRangeLabel } from "../utils/periodFormatting";
import { emptyCard } from "../utils/summaryCards";

// Las filas de Rentabilidad se derivan TODAS desde las cuentas raw del backend
// (ventas_netas, costos_*, gastos_*, *_no_operacional). El store retorna sólo
// estas cuentas planas; los márgenes derivados que viven en tableRowHeaders
// (margen_producto, margen_contribucion) no se llenan con datos —backend no
// los calcula y CALCULATED_KEYS del store los omite. Además, la fórmula del
// store para EBITDA es incorrecta para companies tipo Productos (resta sólo
// costos_directos en lugar de productos+variables+fijos). Recalcular acá nos
// garantiza margenes correctos para ambos tipos de negocio:
//   - Productos: costos_directos viene vacío del backend ⇒ resta 0 ⇒ ok
//   - Servicios: costos_productos/variables/fijos vienen vacíos ⇒ restan 0 ⇒ ok
const GROSS_COSTS = ["costos_productos", "costos_variables", "costos_directos"];
const GROSS_AND_FIXED = [...GROSS_COSTS, "costos_fijos"];
const OPERATIONAL_EXPENSES = ["gastos_administracion", "gastos_comerciales", "gastos_i+d"];
const NET_INCOME_PLUS = ["ventas_netas", "ingresos_no_operacional"];
const NET_INCOME_MINUS = [...GROSS_AND_FIXED, ...OPERATIONAL_EXPENSES, "gastos_no_operacional"];

// Las filas marcadas `productsOnly: true` sólo aplican a companies tipo
// Productos (plan contable con costos_productos / costos_fijos separados).
// Para Servicios el rango se reduce a Ventas → Bruto → EBITDA → Neto, mismo
// criterio que aplica ProfitabilitySummaryCardsUseCase en el backend para el
// breakdown de las cards. Mantener sincronizado: si cambia el spec acá,
// chequear `_margin_spec` en el use case.
const PROFITABILITY_ROW_SPEC = [
	{ id: "ventas_netas", label: "Ventas Netas", isMoney: true,
		derive: { plus: ["ventas_netas"], minus: [] } },

	{ id: "margen_producto_amount", label: "Margen Producto", isMoney: true,
		productsOnly: true,
		derive: { plus: ["ventas_netas"], minus: ["costos_productos"] } },
	{ id: "margen_producto_pct", label: "Margen Producto %", isMoney: false, theme: "theme_row_purple",
		productsOnly: true,
		derive: { plus: ["ventas_netas"], minus: ["costos_productos"], asPercentOf: "ventas_netas" } },

	{ id: "margen_bruto_amount", label: "Margen Bruto", isMoney: true,
		derive: { plus: ["ventas_netas"], minus: GROSS_COSTS } },
	{ id: "margen_bruto_pct", label: "Margen Bruto %", isMoney: false, theme: "theme_row_purple",
		derive: { plus: ["ventas_netas"], minus: GROSS_COSTS, asPercentOf: "ventas_netas" } },

	{ id: "margen_contribucion_amount", label: "Margen Contribución", isMoney: true,
		productsOnly: true,
		derive: { plus: ["ventas_netas"], minus: GROSS_AND_FIXED } },
	{ id: "margen_contribucion_pct", label: "Margen Contribución %", isMoney: false, theme: "theme_row_azure",
		productsOnly: true,
		derive: { plus: ["ventas_netas"], minus: GROSS_AND_FIXED, asPercentOf: "ventas_netas" } },

	{ id: "EBITDA", label: "EBITDA", isMoney: true,
		derive: { plus: ["ventas_netas"], minus: [...GROSS_AND_FIXED, ...OPERATIONAL_EXPENSES] } },
	{ id: "margen_EBITDA_pct", label: "Margen EBITDA %", isMoney: false, theme: "theme_row_purple",
		derive: { plus: ["ventas_netas"], minus: [...GROSS_AND_FIXED, ...OPERATIONAL_EXPENSES],
			asPercentOf: "ventas_netas" } },

	{ id: "utilidad_antes_de_impuestos", label: "Utilidad Antes de Impuestos", isMoney: true,
		derive: { plus: NET_INCOME_PLUS, minus: NET_INCOME_MINUS } },
	{ id: "margen_neto_pct", label: "Margen Neto %", isMoney: false, theme: "theme_row_yellow",
		derive: { plus: NET_INCOME_PLUS, minus: NET_INCOME_MINUS, asPercentOf: "ventas_netas" } },
];

// Placeholder cuando el endpoint aún no respondió (mounted inicial). Devuelve
// una card "vacía" pero estructuralmente válida para que CardVentas renderice
// sin v-if/v-else fragmentado en el template. Misma técnica que NoOperTab.
const EMPTY_CARD = () => emptyCard();

export default {
	name: "RentabilidadTab",

	components: {
		TabHeader,
		ChartLine,
		Layers,
		CardVentas,
		FinancialResultsTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", [
			"filterPeriod",
			"tableRowHeaders",
			"isLoading",
		]),
		...mapState("profitabilityReport", ["summaryCards"]),
		...mapGetters("financialReports", {
			getHeadersTableFunc: "getHeadersTable",
		}),

		getNow() {
			return moment().toDate();
		},

		getHeadersTable() {
			return this.getHeadersTableFunc();
		},

		// Discriminador del tipo de negocio derivado del `all_keys` que backend
		// devuelve en getResultStateByPeriod: solo Productos incluye
		// `margen_producto` en all_keys, lo que marca isShowed=true en la fila
		// del store. Antes del primer fetch isShowed es undefined → tratamos
		// como Servicios (caso degradado más conservador: oculta filas en vez
		// de mostrar valores duplicados).
		isProductsBusiness() {
			return this.tableRowHeaders?.margen_producto?.isShowed === true;
		},

		profitabilityRows() {
			const isProducts = this.isProductsBusiness;
			return PROFITABILITY_ROW_SPEC
				.filter(spec => isProducts || !spec.productsOnly)
				.map(spec => ({
					id: spec.id,
					label: spec.label,
					isMoney: spec.isMoney,
					theme: spec.theme,
					itemsDict: this.deriveItemsDict(spec.derive),
				}));
		},

		profitabilityCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},

		fetchSignature() {
			return [this.filterPeriod, this.endMonth, this.selectedPeriodRange].join("|");
		},

		// Las cards sólo dependen del mes (rangos current_month/YTD/LTM se
		// calculan en backend). Aislar la firma evita refetches cuando cambia
		// granularidad o rango de la tabla.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	mounted() {
		this.fetchReportData();
		this.fetchProfitabilitySummary();
	},

	methods: {
		...mapActions("financialReports", ["fetchReportData"]),
		...mapActions("profitabilityReport", ["fetchProfitabilitySummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateSelectedPeriodRange(range) {
			this.setPeriodRange(range);
		},

		// Mapeo card del backend → shape de CardVentas. Mismo patrón que
		// NoOperTab.buildCard. El index del breakdown asigna color por slot
		// (--progress-bar-1..5) — backend garantiza orden Producto→Bruto→...→Neto.
		buildCard(card, { showMom = false } = {}) {
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				progressItems: (card.breakdown || []).map((item, i) => ({
					label: item.label,
					percent: item.percent,
					amount: item.amount,
					color: PROFITABILITY_CARD_PROGRESS_COLORS[i % PROFITABILITY_CARD_PROGRESS_COLORS.length],
				})),
			};
		},

		// Combina cuentas raw del store: sum(plus) - sum(minus) por header.
		// Si asPercentOf viene, divide por ese row.itemsDict[header] * 100
		// (escala que el renderer espera: Math.round(val) + '%'). Si el
		// denominador es 0, devuelve 0 — sin throw, sin NaN renderizado.
		deriveItemsDict({ plus = [], minus = [], asPercentOf = null }) {
			const headers = this.getHeadersTable;
			const result = {};
			const sumByKeys = (keys, header) => keys.reduce((acc, key) => {
				const row = this.tableRowHeaders[key] || { itemsDict: {} };
				return acc + Number(row.itemsDict[header] ?? 0);
			}, 0);
			headers.forEach(header => {
				const value = sumByKeys(plus, header) - sumByKeys(minus, header);
				if (asPercentOf) {
					const denom = Number(
						(this.tableRowHeaders[asPercentOf] || { itemsDict: {} }).itemsDict[header] ?? 0
					);
					result[header] = denom !== 0 ? (value / denom) * 100 : 0;
				} else {
					result[header] = value;
				}
			});
			return result;
		},
	},

	watch: {
		fetchSignature() {
			this.fetchReportData();
		},
		summarySignature() {
			this.fetchProfitabilitySummary();
		},
	},
};
</script>

<style scoped lang="scss">
@import "@/assets/style.scss";

.rentabilidad_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue.

// Section title estilo nuevo en Rentabilidad — agrupa cards y tabla bajo
// títulos con icono. Si se reusa en otro tab, mover a una clase utility
// global y eliminar duplicación.
.section_title {
	display: flex;
	align-items: center;
	font-size: 16px;
	font-weight: 600;
	color: var(--report-text);
}

.section_icon {
	width: 18px;
	height: 18px;
	color: var(--report-text-muted);
}

.title_icon {
	width: 18px;
	height: 18px;
	color: var(--report-text-muted);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}

.cards_container {
	width: 100%;
	flex-wrap: wrap;
}

.action_btn {
	background: white;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover:not(:disabled) { background: var(--report-surface); }

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
}
</style>
